import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FaArrowRight } from 'react-icons/fa';
import { projects } from '../data/projects';

const PortfolioPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get unique categories
  const categories = ['all', ...new Set(projects.map(p => p.category))];

  // Filter projects
  const filteredProjects = selectedCategory === 'all'
    ? projects
    : projects.filter(p => p.category === selectedCategory);

  return (
    <>
      <Helmet>
        <title>Portfolio | Nos Réalisations Web & Apps | Olney Tech Solutions</title>
        <meta name="description" content="Découvrez nos réalisations : sites e-commerce, applications web, chatbots IA, infrastructures cloud. Études de cas détaillées avec résultats mesurables." />
        <link rel="canonical" href="https://olney.dev/portfolio" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[var(--secondary)] rounded-full blur-[150px] opacity-10 pointer-events-none"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-block mb-4 px-4 py-1 rounded-full border border-[var(--secondary)] bg-[rgba(0,198,255,0.1)] text-[var(--secondary)] text-sm font-bold tracking-widest uppercase">
              Portfolio
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 font-['Space_Grotesk'] text-white">
              Nos <span className="text-gradient-electric">Réalisations</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Chaque projet raconte une histoire de transformation.
              Découvrez comment nous avons aidé nos clients à atteindre leurs objectifs.
            </p>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-[var(--primary)] text-white'
                    : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {category === 'all' ? 'Tous' : category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCategory}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/portfolio/${project.slug}`}
                    className="glass-panel rounded-3xl overflow-hidden block hover:border-[var(--primary)]/50 transition-all group"
                  >
                    {/* Thumbnail */}
                    <div className="aspect-video bg-gradient-to-br from-[var(--primary)]/20 to-[var(--secondary)]/20 relative overflow-hidden">
                      <img
                        src={project.thumbnail}
                        alt={project.title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#030014] to-transparent opacity-60"></div>

                      {/* Category Badge */}
                      <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[var(--primary)]/80 text-white text-xs font-bold uppercase tracking-wider backdrop-blur-sm">
                        {project.category}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2 font-['Space_Grotesk'] group-hover:text-[var(--primary)] transition-colors">
                        {project.title}
                      </h3>
                      <p className="text-gray-500 text-sm mb-4">
                        {project.client} • {project.year}
                      </p>

                      {/* Results Preview */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        {project.results.slice(0, 3).map((result, idx) => (
                          <div key={idx} className="text-center p-2 rounded-lg bg-white/5">
                            <div className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] font-bold text-sm">
                              {result.metric}
                            </div>
                            <div className="text-gray-500 text-xs truncate">{result.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.slice(0, 3).map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 rounded-md bg-white/5 text-gray-400 text-xs"
                          >
                            {tech}
                          </span>
                        ))}
                        {project.technologies.length > 3 && (
                          <span className="px-2 py-1 rounded-md bg-white/5 text-gray-400 text-xs">
                            +{project.technologies.length - 3}
                          </span>
                        )}
                      </div>

                      {/* CTA */}
                      <span className="text-[var(--primary)] font-bold text-sm inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                        Voir l'étude de cas <FaArrowRight />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-panel rounded-3xl p-12 text-center border-[var(--primary)]/30"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 font-['Space_Grotesk']">
              Votre projet pourrait être le prochain
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Discutons de vos objectifs et créons ensemble quelque chose d'exceptionnel.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="btn-magnetic px-8 py-4 bg-[var(--primary)] text-white font-bold rounded-xl"
              >
                Démarrer un projet
              </Link>
              <a
                href="tel:+33689177385"
                className="px-8 py-4 border border-white/20 text-white font-bold rounded-xl hover:bg-white/5 transition-all"
              >
                06 89 17 73 85
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default PortfolioPage;
